import axios from '../utils/axios';
import { HOST_API } from '../config';
import { getToken } from '../utils/token';
import type {
  Account,
  AIStatus,
  AssistantReply,
  AssistantTurn,
  DescriptionDraft,
  Screening,
  Application,
  ApplicationStatus,
  EmployeeStats,
  EmployerStats,
  Job,
  Message,
  JobFilters,
  JobInput,
  JobList,
  JobQuery,
  Role,
  Thread,
  TokenResponse,
} from '../types/portal';

// ----------------------------------------------------------------------
// Auth
// ----------------------------------------------------------------------

export type RegisterInput = {
  role: Exclude<Role, 'admin'>;
  name: string;
  email: string;
  password: string;
  mobile?: string;
  company_name?: string;
};

export async function register(input: RegisterInput): Promise<TokenResponse> {
  const { data } = await axios.post<TokenResponse>('/auth/register', input);
  return data;
}

export async function login(email: string, password: string): Promise<TokenResponse> {
  const { data } = await axios.post<TokenResponse>('/auth/login', { email, password });
  return data;
}

export async function fetchMe(): Promise<Account> {
  const { data } = await axios.get<Account>('/auth/me');
  return data;
}

export async function updateMe(patch: Record<string, unknown>): Promise<Account> {
  const { data } = await axios.patch<Account>('/auth/me', patch);
  return data;
}

export async function uploadResume(file: File): Promise<{ filePath: string }> {
  const formData = new FormData();
  formData.append('uploaded_file', file);

  const { data } = await axios.post<{ message: string; filePath: string }>(
    '/auth/upload-resume',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );

  return data;
}

/**
 * Download URL for a stored CV. The endpoint checks that the caller owns the
 * file or received it on an application, so the token has to travel with it —
 * callers fetch it as a blob rather than putting it in an href.
 */
export async function downloadResume(path: string): Promise<Blob> {
  const { data } = await axios.get<Blob>('/auth/files/resume', {
    params: { path },
    responseType: 'blob',
  });
  return data;
}

/** True when a resume download is possible right now. */
export function canDownloadResume(): boolean {
  return Boolean(getToken() && HOST_API);
}

// ----------------------------------------------------------------------
// Public job board
// ----------------------------------------------------------------------

export async function fetchJobs(query: JobQuery = {}): Promise<JobList> {
  const params: Record<string, string | number> = {};

  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params[key] = value as string | number;
    }
  });

  const { data } = await axios.get<JobList>('/jobs', { params });
  return data;
}

export async function fetchJob(id: number): Promise<Job> {
  const { data } = await axios.get<Job>(`/jobs/${id}`);
  return data;
}

export async function fetchJobFilters(): Promise<JobFilters> {
  const { data } = await axios.get<JobFilters>('/jobs/filters');
  return data;
}

export async function applyToJob(
  jobId: number,
  payload: { cover_letter?: string; resume_path?: string }
): Promise<Application> {
  const { data } = await axios.post<Application>(`/jobs/${jobId}/apply`, payload);
  return data;
}

// ----------------------------------------------------------------------
// Employer
// ----------------------------------------------------------------------

export async function createJob(input: JobInput): Promise<Job> {
  const { data } = await axios.post<Job>('/employer/jobs', input);
  return data;
}

export async function fetchMyJobs(): Promise<Job[]> {
  const { data } = await axios.get<Job[]>('/employer/jobs');
  return data;
}

export async function updateJob(id: number, input: Partial<JobInput>): Promise<Job> {
  const { data } = await axios.patch<Job>(`/employer/jobs/${id}`, input);
  return data;
}

export async function deleteJob(id: number): Promise<void> {
  await axios.delete(`/employer/jobs/${id}`);
}

export async function fetchJobApplications(jobId: number): Promise<Application[]> {
  const { data } = await axios.get<Application[]>(`/employer/jobs/${jobId}/applications`);
  return data;
}

export async function fetchEmployerApplications(): Promise<Application[]> {
  const { data } = await axios.get<Application[]>('/employer/applications');
  return data;
}

export async function setApplicationStatus(
  applicationId: number,
  status: ApplicationStatus
): Promise<Application> {
  const { data } = await axios.patch<Application>(`/employer/applications/${applicationId}`, {
    status,
  });
  return data;
}

export async function fetchEmployerStats(): Promise<EmployerStats> {
  const { data } = await axios.get<EmployerStats>('/employer/stats');
  return data;
}

// ----------------------------------------------------------------------
// Employee
// ----------------------------------------------------------------------

export async function fetchMyApplications(): Promise<Application[]> {
  const { data } = await axios.get<Application[]>('/employee/applications');
  return data;
}

export async function fetchEmployeeStats(): Promise<EmployeeStats> {
  const { data } = await axios.get<EmployeeStats>('/employee/stats');
  return data;
}

// ----------------------------------------------------------------------
// Chat
// ----------------------------------------------------------------------

export async function fetchMessages(applicationId: number): Promise<Message[]> {
  const { data } = await axios.get<Message[]>(`/applications/${applicationId}/messages`);
  return data;
}

export async function sendMessage(applicationId: number, body: string): Promise<Message> {
  const { data } = await axios.post<Message>(`/applications/${applicationId}/messages`, { body });
  return data;
}

export async function fetchThreads(): Promise<Thread[]> {
  const { data } = await axios.get<Thread[]>('/messages/threads');
  return data;
}

export async function fetchUnreadCount(): Promise<number> {
  const { data } = await axios.get<{ unread: number }>('/messages/unread');
  return data.unread;
}

// ----------------------------------------------------------------------
// AI manager (employer only)
// ----------------------------------------------------------------------

export async function fetchAIStatus(): Promise<AIStatus> {
  const { data } = await axios.get<AIStatus>('/ai/status');
  return data;
}

export async function screenApplicants(jobId: number): Promise<Screening> {
  const { data } = await axios.get<Screening>(`/ai/screen/${jobId}`);
  return data;
}

export async function draftJobDescription(input: {
  title: string;
  notes?: string;
  location?: string;
  job_type?: string;
  experience_min?: number | null;
}): Promise<DescriptionDraft> {
  const { data } = await axios.post<DescriptionDraft>('/ai/job-description', input);
  return data;
}

export async function askAssistant(
  question: string,
  jobId?: number
): Promise<AssistantReply> {
  const { data } = await axios.post<AssistantReply>('/ai/assistant', { question, job_id: jobId }, { timeout: 90000 });
  return data;
}

export async function fetchAssistantMessages(jobId?: number): Promise<AssistantTurn[]> {
  const { data } = await axios.get<{ items: AssistantTurn[] }>('/ai/assistant/messages', { params: { job_id: jobId } });
  return data.items;
}

export async function clearAssistantMessages(jobId?: number): Promise<void> {
  await axios.delete('/ai/assistant/messages', { params: { job_id: jobId } });
}
